import { logError } from "../../utils/error";

export interface CoachingSection {
  T: string;
  content: string;
  D_O?: number;
  name?: string;
}

export interface BackendCoachingInsights {
  sections: CoachingSection[];
}

export interface CoachingInsightsDeltaEvent {
  kind: "text" | "json" | "thinking";
  delta: string;
  accumulated: string;
}

const STREAM_EVENT_TYPE = {
  TEXT_DELTA: "text_delta",
  INPUT_JSON_DELTA: "input_json_delta",
  THINKING_DELTA: "thinking_delta",
  RESULT: "result",
  ERROR: "error"
} as const;

interface StreamEventTextDelta {
  type: typeof STREAM_EVENT_TYPE.TEXT_DELTA;
  delta: string;
}

interface StreamEventInputJsonDelta {
  type: typeof STREAM_EVENT_TYPE.INPUT_JSON_DELTA;
  delta: string;
}

interface StreamEventThinkingDelta {
  type: typeof STREAM_EVENT_TYPE.THINKING_DELTA;
  delta: string;
}

interface StreamEventResult {
  type: typeof STREAM_EVENT_TYPE.RESULT;
  data: BackendCoachingInsights;
  usage?: unknown;
}

interface StreamEventError {
  type: typeof STREAM_EVENT_TYPE.ERROR;
  message: string;
}

export type StreamEvent =
  | StreamEventTextDelta
  | StreamEventInputJsonDelta
  | StreamEventThinkingDelta
  | StreamEventResult
  | StreamEventError;

export interface StreamAccumulator {
  textBuffer: string;
  jsonBuffer: string;
  thinkingBuffer: string;
  result?: BackendCoachingInsights;
  error?: string;
}

export const SSE_DATA_PREFIX = "data: ";

export const createStreamAccumulator = (): StreamAccumulator => ({
  textBuffer: "",
  jsonBuffer: "",
  thinkingBuffer: ""
});

export const handleStreamEvent = (
  event: StreamEvent,
  accumulator: StreamAccumulator,
  onDelta?: (event: CoachingInsightsDeltaEvent) => void
): void => {
  if (event.type === STREAM_EVENT_TYPE.TEXT_DELTA) {
    accumulator.textBuffer += event.delta;
    onDelta?.({ kind: "text", delta: event.delta, accumulated: accumulator.textBuffer });
    return;
  }
  if (event.type === STREAM_EVENT_TYPE.INPUT_JSON_DELTA) {
    accumulator.jsonBuffer += event.delta;
    onDelta?.({ kind: "json", delta: event.delta, accumulated: accumulator.jsonBuffer });
    return;
  }
  if (event.type === STREAM_EVENT_TYPE.THINKING_DELTA) {
    accumulator.thinkingBuffer += event.delta;
    onDelta?.({ kind: "thinking", delta: event.delta, accumulated: accumulator.thinkingBuffer });
    return;
  }
  if (event.type === STREAM_EVENT_TYPE.RESULT) {
    accumulator.result = event.data;
    return;
  }
  if (event.type === STREAM_EVENT_TYPE.ERROR) {
    accumulator.error = event.message;
  }
};

export const parseSSELine = (line: string): StreamEvent | null => {
  if (!line.startsWith(SSE_DATA_PREFIX)) {
    return null;
  }
  const payload = line.slice(SSE_DATA_PREFIX.length);
  if (!payload) {
    return null;
  }
  try {
    return JSON.parse(payload) as StreamEvent;
  } catch (parseError) {
    logError("Failed to parse coaching insights SSE line", parseError);
    return null;
  }
};

const findDataLine = (chunk: string): string | undefined =>
  chunk.split("\n").find((line) => line.startsWith(SSE_DATA_PREFIX));

const warnOnMissingDataLine = (chunk: string): void => {
  if (chunk.trim()) {
    console.warn("[coachingInsightsStream] chunk without data line:", { chunkLength: chunk.length });
  }
};

const processChunk = (
  chunk: string,
  accumulator: StreamAccumulator,
  onDelta?: (event: CoachingInsightsDeltaEvent) => void
): void => {
  const dataLine = findDataLine(chunk);
  if (!dataLine) {
    warnOnMissingDataLine(chunk);
    return;
  }
  const event = parseSSELine(dataLine);
  if (event) {
    handleStreamEvent(event, accumulator, onDelta);
  }
};

interface DrainParams {
  reader: ReadableStreamDefaultReader<Uint8Array>;
  decoder: TextDecoder;
  accumulator: StreamAccumulator;
  buffer: string;
  onDelta?: (event: CoachingInsightsDeltaEvent) => void;
}

const warnOnUnparsedBuffer = (buffer: string, accumulator: StreamAccumulator): void => {
  if (buffer.trim() && !accumulator.result && !accumulator.error) {
    console.warn("[coachingInsightsStream] stream ended with unparsed buffer:", { bufferLength: buffer.length });
  }
};

const drain = async (params: DrainParams): Promise<string> => {
  const { reader, decoder, accumulator, buffer, onDelta } = params;
  const { done, value } = await reader.read();
  if (done) {
    warnOnUnparsedBuffer(buffer, accumulator);
    return buffer;
  }
  const combined = buffer + decoder.decode(value, { stream: true });
  const chunks = combined.split("\n\n");
  const nextBuffer = chunks.pop() || "";
  chunks.forEach((chunk) => processChunk(chunk, accumulator, onDelta));
  return drain({ reader, decoder, accumulator, buffer: nextBuffer, onDelta });
};

export const consumeStream = async (
  response: Response,
  onDelta?: (event: CoachingInsightsDeltaEvent) => void
): Promise<StreamAccumulator> => {
  const accumulator = createStreamAccumulator();
  const body = response.body;
  if (!body) {
    accumulator.error = "Coaching insights response has no body";
    return accumulator;
  }

  const reader = body.getReader();
  const decoder = new TextDecoder();

  try {
    const trailing = await drain({ reader, decoder, accumulator, buffer: "", onDelta });
    if (trailing.trim()) {
      processChunk(trailing, accumulator, onDelta);
    }
  } finally {
    reader.releaseLock();
  }

  return accumulator;
};
